import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { replaceCanvas, updateNodeData } from '../../../../store/workflow/workflowSlice';
import type { RootState } from '../../../../store';
import { resolveEndpoint, assumedSamColumns, Endpoint, Column } from '../../utils/transformLoad';
import { DEFAULT_SPLIT, SplitSpec, buildSplitFlow, splitContext, splitModesFor } from '../../utils/splitFlow';
import { checkTransformLoad, CheckItem } from '../../utils/transformLoadCheck';
import { fetchCsvColumns } from '../../../../utils/csvColumnsService';
import DataDetailModal from './DataDetailModal';
import TransformMappingModal, { MappingItem } from './TransformMappingModal';
import SqlEditorModal from './SqlEditorModal';
import { be1Fetch } from '../../../../config/api';
import { Collapsible, FieldLabel, PanelHeader, Section, inputCls, primaryBtn, smallBtn } from './ui';
import { useTranslation } from 'react-i18next';
import { typeLabelOf } from '../../nodeCatalog';
import { isErrorEdge } from '../../utils/errorFlow';
import MappingSpecPicker from '../../../mappingSpec/MappingSpecPicker';
import { SpecRef, changedTargets, loadLatestCached, specToMapping } from '../../../../services/mappingSpec';
import { showAlert, showConfirm } from '../../../common/AppDialog';

/** 화면 문구 키 — 소스·타깃이 무엇인지, 두 DB 가 어떤 사이인지 (모듈 상수라 키만 두고 렌더에서 번역한다) */
const KIND_KEY: Record<string, string> = { sam: 'property:kind.sam', db: 'property:kind.db', inner: 'property:kind.inner', none: 'property:kind.none' };
const REL_KEY: Record<string, string> = {
  same: 'property:relation.same', homogeneous: 'property:relation.homogeneous', heterogeneous: 'property:relation.heterogeneous',
};

interface Props { node: any }

/** 소스 SQL 초안 — 알고 있는 컬럼을 늘어놓는다. 컬럼을 모르면 * */
export function draftSelect(ep: Endpoint | null, columns: Column[]): string {
  const c = ep?.node?.data?.config || {};
  const tbl = `${c.schema ? `${c.schema}.` : ''}${c.table || 'table_name'}`;
  if (!columns.length) return `SELECT *\nFROM ${tbl}`;
  return `SELECT ${columns.map((x) => x.name).join(',\n       ')}\nFROM ${tbl}`;
}

/**
 * 데이터처리 노드 속성.
 *
 * 순서(현장 요청 2026-09-17): 소스 → 타깃 → 점검 → 추출 방식 → 항목 매핑 및 변환 → 적재 방식 → 분할처리(접힘).
 * 소스·타깃은 입력하지 않는다: 엣지로 연결된 SAM파일·DB테이블(없으면 선행/후속 노드의 내부데이터)을 보여 주고
 * "상세보기"로 항목·값을 연다. '점검' 은 실행 전에 논리적으로 어긋난 곳(없는 컬럼 매핑, 같은 테이블 WRITE,
 * 컬럼 변경 뒤 미확인, 문자→날짜 등)을 알려 준다 — 판정은 utils/transformLoadCheck.
 */
const TransformLoadPanel: React.FC<Props> = ({ node }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const nodes = useSelector((s: any) => s.workflow.nodes);
  const edges = useSelector((s: any) => s.workflow.edges);
  const connections = useSelector((s: RootState) => s.connections.list);
  const projectId = useSelector((s: RootState) => s.project.selectedProjectId);
  // 예외 흐름으로 이어 둔 노드 이름 — 무엇이 실패 때 도는지 속성에서 바로 보이게 (utils/errorFlow)
  const errorTargets: string[] = edges
    .filter((e: any) => e.source === node.id && isErrorEdge(e))
    .map((e: any) => nodes.find((n: any) => n.id === e.target)?.data?.label || e.target);
  const cfg = node?.data?.config || {};

  const source = useMemo(() => resolveEndpoint(node.id, 'up', nodes, edges), [node.id, nodes, edges]);
  const target = useMemo(() => resolveEndpoint(node.id, 'down', nodes, edges), [node.id, nodes, edges]);

  const patch = useCallback((p: Record<string, any>) => {
    const next = { ...cfg, ...p };
    dispatch(updateNodeData({ id: node.id, data: { config: next, ...(p.label !== undefined ? { label: p.label } : {}) } } as any));
  }, [cfg, dispatch, node.id]);

  // ── 분할처리: 지금 소스·타깃 조합에서 고를 수 있는 방식과, 고른 방식의 세부 값
  const splitCtx = useMemo(() => splitContext(source, target, connections as any), [source, target, connections]);
  const splitModes = useMemo(() => splitModesFor(splitCtx), [splitCtx]);
  const [splitSpec, setSplitSpec] = useState<SplitSpec>({ ...DEFAULT_SPLIT, ...(cfg.split || {}) });
  const splitNeeds = splitModes.find((m) => m.mode === splitSpec.mode)?.needs || [];
  const splitBlockedReason = useMemo(() => {
    const info = splitModes.find((m) => m.mode === splitSpec.mode);
    if (info?.blocked) return info.blocked;
    if (splitNeeds.includes('keyColumn') && !splitSpec.keyColumn) return t('property:split.needKey');
    if (splitNeeds.includes('dbLinkName') && !(splitSpec.dbLinkName || '').trim()) return t('property:split.needDbLink');
    if (splitNeeds.includes('count') && (splitSpec.count < 2 || splitSpec.count > 32)) return t('property:split.countRange');
    return '';
  }, [splitModes, splitSpec, splitNeeds, t]);

  /**
   * 분할처리 적용 — 이 노드를 지우고, 자동 생성된 흐름(읽기 전용 그룹 + 자식)으로 바꾼다.
   * 바깥에서 들어오던 엣지는 그룹 안 첫 노드로, 나가던 엣지는 마지막 노드에서 나가도록 다시 잇는다.
   */
  const applySplit = async () => {
    if (splitBlockedReason) return;
    // 그룹 이름은 캔버스에 저장된다 — 만드는 순간의 언어로 정해지고 뒤에 언어를 바꿔도 그대로다(docs/I18N.md)
    const groupLabel = t('workflow:split.groupLabel', { name: node.data?.label || typeLabelOf('transformLoad') });
    const built = buildSplitFlow(splitSpec, splitCtx, cfg, groupLabel);
    if (!built.entryId || !built.exitId) return;

    // 그룹은 이 노드 자리에서 시작한다 (자식 좌표는 그룹 기준 상대 좌표)
    const group = built.nodes[0];
    group.position = { ...node.position };

    // 그룹은 노드 하나보다 훨씬 넓다 — 오른쪽 노드들을 그만큼 밀어 두지 않으면 펼쳤을 때 겹친다.
    // (접혀 있는 동안에는 캔버스가 다시 당겨 그리므로 접힌 모습의 간격은 그대로다)
    const grew = Math.max(0, Number((group.style as any)?.width ?? 300) - 105);
    const rightOf = node.position.x + 105;
    const nextNodes = [
      ...nodes
        .filter((n: any) => n.id !== node.id)
        .map((n: any) => (!n.parentId && n.position?.x >= rightOf
          ? { ...n, position: { x: n.position.x + grew, y: n.position.y } }
          : n)),
      ...built.nodes,
    ];
    const nextEdges = [
      ...edges
        .filter((e: any) => e.source === node.id || e.target === node.id)
        .map((e: any) => (e.target === node.id
          ? { ...e, target: built.entryId, id: `e_${e.source}_${built.entryId}` }
          : { ...e, source: built.exitId, id: `e_${built.exitId}_${e.target}` })),
      ...edges.filter((e: any) => e.source !== node.id && e.target !== node.id),
      ...built.edges,
    ];
    dispatch(replaceCanvas({ nodes: nextNodes as any, edges: nextEdges as any }));
  };

  // SAM파일 항목은 서버에서 조회 (노드에 데이터 정의 항목이 이미 있으면 그것을 우선)
  const [samCols, setSamCols] = useState<Record<string, { columns?: Column[]; error?: string; loading?: boolean }>>({});
  const samKey = (ep: Endpoint | null) => {
    if (!ep || ep.kind !== 'sam') return '';
    const c = ep.node.data?.config || {};
    return `${c.filepath}|${c.delimiter || ','}|${c.hasHeader !== false}|${c.encoding || 'UTF-8'}`;
  };
  const loadSam = useCallback((ep: Endpoint | null) => {
    const key = samKey(ep);
    // 이미 결과(항목·조회 중·오류)가 있으면 다시 하지 않는다 — 오류를 빼먹으면 경로 없는 SAM파일에서
    // 오류 기록 → samCols 변경 → loadSam 변경 → 다시 기록 으로 끝없이 돌았다(Maximum update depth)
    if (!ep || !key || samCols[key]) return;
    const c = ep.node.data?.config || {};
    if (!c.filepath) { setSamCols((m) => ({ ...m, [key]: { error: t('property:sam.noPath') } })); return; }
    setSamCols((m) => ({ ...m, [key]: { loading: true } }));
    fetchCsvColumns(c.filepath, c.delimiter || ',', c.hasHeader !== false, c.encoding || 'UTF-8')
      .then((r) => setSamCols((m) => ({ ...m, [key]: { columns: r.columns } })))
      .catch((e) => setSamCols((m) => ({ ...m, [key]: { error: t('property:sam.columnsFailed', { message: e.message }) } })));
  }, [samCols, t]);
  useEffect(() => { loadSam(source); loadSam(target); }, [source, target, loadSam]);

  const columnsOf = (ep: Endpoint | null): { columns: Column[]; loading?: boolean; error?: string; assumed?: boolean } => {
    if (!ep) return { columns: [] };
    if (ep.kind === 'sam') {
      const st = samCols[samKey(ep)];
      if (st?.columns?.length) return { columns: st.columns };
      if (ep.columns.length) return { columns: ep.columns };
      // 파일이 아직 없으면(서버에서 못 읽으면) **이 워크플로가 만들 파일**이다 — 앞 노드가 내보내는
      // 항목을 그대로 가정해 매핑을 짤 수 있게 한다. 가정이라는 사실은 화면에 적는다.
      if (!st?.loading) {
        const assumed = assumedSamColumns(ep.node.id, nodes, edges);
        if (assumed.length) return { columns: assumed, assumed: true };
      }
      return { columns: [], loading: st?.loading, error: st?.error };
    }
    return { columns: ep.columns };
  };
  const srcInfo = columnsOf(source);
  const tgtInfo = columnsOf(target);

  const [detail, setDetail] = useState<'source' | 'target' | null>(null);
  const [mappingOpen, setMappingOpen] = useState(false);
  const [sqlOpen, setSqlOpen] = useState(false);
  // 매핑 제안이 값 패턴(날짜 형식·길이 초과·공백 패딩)을 보려면 소스 샘플이 필요하다.
  // 팝업을 열 때 한 번만 읽는다 — 실패해도 제안은 이름·타입 기준으로 계속 동작한다.
  const [sourceRows, setSourceRows] = useState<Array<Record<string, unknown>>>([]);
  const openMapping = useCallback(async () => {
    setMappingOpen(true);
    if (!source || source.kind === 'inner') { setSourceRows([]); return; }
    const c = source.node.data?.config || {};
    try {
      const body = source.kind === 'db'
        ? { kind: 'db', connection: c.connection, schema: c.schema, table: c.table, limit: 50 }
        : { kind: 'csv', filepath: c.filepath, delimiter: c.delimiter || ',', hasHeader: c.hasHeader !== false, fieldNames: c.fieldNames || undefined, encoding: c.encoding || 'UTF-8', limit: 50 };
      const r = await be1Fetch('/api/data/preview', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      const d = await r.json();
      setSourceRows(r.ok && Array.isArray(d.rows) ? d.rows : []);
    } catch {
      setSourceRows([]);
    }
  }, [source]);
  const [pickerOpen, setPickerOpen] = useState(false);
  useEffect(() => { setDetail(null); setMappingOpen(false); setSqlOpen(false); setPickerOpen(false); }, [node.id]);

  // 타깃 DB 커넥션 유형 → 벌크 로더 라벨
  const targetConn = target?.kind === 'db' ? connections.find((c: any) => c.connection_id === target.node.data?.config?.connection) : undefined;
  const ctype = (targetConn?.conn_type || '').toLowerCase();
  const bulk = ctype.includes('oracle') ? { ok: true, label: t('property:bulk.oracle') }
    : ctype.includes('postgres') ? { ok: true, label: t('property:bulk.postgres') }
    : { ok: false, label: t('property:bulk.unsupported', { type: ctype || t('property:bulk.unknownType') }) };
  // 직접 경로 적재(APPEND_VALUES) — Oracle 만(티베로 제외). UPSERT·벌크 로더(이미 직접 경로)와는 함께 쓰지 않는다
  const directOk = ctype.includes('oracle') && !ctype.includes('tibero');

  const mapping: MappingItem[] = Array.isArray(cfg.mapping) ? cfg.mapping : [];
  const transformMode = cfg.transformMode === 'MAPPING' ? 'MAPPING' : 'MOVE';
  const mode = cfg.mode === 'APPEND' ? 'APPEND' : cfg.mode === 'UPSERT' ? 'UPSERT' : 'WRITE';

  // ── 매핑 설계서 — 노드는 판을 정해 가져온 사본으로 돈다. 최신 판이 다르면 알리고, 반영은 사람이 누른다
  const specRef: SpecRef | null = cfg.mappingSpec && cfg.mappingSpec.mappingId ? cfg.mappingSpec : null;
  const [specState, setSpecState] = useState<{ latest: number; changed: number; missing: boolean } | null>(null);
  useEffect(() => {
    setSpecState(null);
    if (!specRef || transformMode !== 'MAPPING') return;
    let alive = true;
    loadLatestCached(specRef.projectId).then((latest) => {
      if (!alive) return;
      const has = latest.tables.some((x) => x.mappingId.trim() === specRef.mappingId);
      const built = has ? specToMapping(latest, specRef.mappingId).items : [];
      setSpecState({ latest: latest.version, changed: has ? changedTargets(mapping, built) : 0, missing: !has });
    }).catch(() => { /* 설계서를 못 읽어도 노드는 들고 있는 사본으로 돈다 */ });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [specRef?.projectId, specRef?.mappingId, specRef?.version, transformMode, JSON.stringify(mapping)]);
  const applySpec = (r: { mappingId: string; version: number; items: MappingItem[] }) => {
    patch({ mapping: r.items, transformMode: 'MAPPING', mappingSyncedAt: new Date().toISOString(),
      mappingSpec: { projectId: specRef?.projectId || projectId, mappingId: r.mappingId, version: r.version } });
    setPickerOpen(false);
  };
  const refreshFromSpec = async () => {
    if (!specRef) return;
    const latest = await loadLatestCached(specRef.projectId, 0);
    const built = specToMapping(latest, specRef.mappingId);
    if (built.problems.length || !built.items.length) {
      showAlert(t('mapping:node.refreshBlocked', { id: specRef.mappingId, v: latest.version }));
      return;
    }
    applySpec({ mappingId: specRef.mappingId, version: latest.version, items: built.items });
  };
  /** 설계서에서 가져온 매핑을 손으로 고치면 설계서와 끊는다 — 묻고 */
  const openManual = async () => {
    if (specRef && !(await showConfirm(t('mapping:node.unlinkConfirm', { id: specRef.mappingId })))) return;
    openMapping();
  };

  // ── 점검
  const issues: CheckItem[] = useMemo(
    () => checkTransformLoad({ source, target, sourceColumns: srcInfo.columns, targetColumns: tgtInfo.columns, cfg }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [source, target, srcInfo.columns, tgtInfo.columns, cfg],
  );
  const errors = issues.filter((i) => i.level === 'error').length;
  const warns = issues.filter((i) => i.level === 'warn').length;
  const hasColumnChange = issues.some((i) => i.code === 'columnsChanged');

  /** SQL 쿼리로 바꿀 때 비어 있으면 초안을 넣는다 — 알고 있는 컬럼을 늘어놓은 SELECT */
  const chooseQueryMode = () => {
    const p: Record<string, any> = { extractMode: 'QUERY' };
    if (!(cfg.query || '').trim()) p.query = draftSelect(source, srcInfo.columns);
    patch(p);
  };

  const EndpointBox = ({ title, ep, info, which }: { title: string; ep: Endpoint | null; info: typeof srcInfo; which: 'source' | 'target' }) => (
    <Section title={title} right={
      <button type="button" className={smallBtn} disabled={!ep} onClick={() => setDetail(which)}>{t('property:endpoint.detail')}</button>
    }>
      {ep ? (
        <div className="grid grid-cols-[64px_1fr] gap-y-0.5 text-xs">
          <span className="text-gray-500">{t('property:endpoint.type')}</span>
          <span className="text-gray-800 font-medium">{ep.kindLabel}</span>
          <span className="text-gray-500">{ep.kind === 'sam' ? t('property:endpoint.file') : ep.kind === 'db' ? t('property:endpoint.table') : t('property:endpoint.node')}</span>
          <span className="text-gray-800 truncate" title={ep.name}>{ep.name}</span>
          <span className="text-gray-500">{t('property:endpoint.columns')}</span>
          <span className="text-gray-600">
            {info.loading ? t('property:endpoint.loading') : info.error && !info.columns.length ? <span className="text-red-500">{info.error}</span> : t('property:endpoint.columnCount', { n: info.columns.length })}
            {info.assumed && <span className="ml-1 text-amber-600" title={t('property:endpoint.assumedTitle')}>{t('property:endpoint.assumed')}</span>}
          </span>
        </div>
      ) : (
        <div className="text-xs text-gray-400">{which === 'source' ? t('property:endpoint.noSource') : t('property:endpoint.noTarget')}</div>
      )}
    </Section>
  );

  return (
    <div className="h-full bg-white p-4 overflow-auto">
      <PanelHeader typeLabel={t('property:tl.typeLabel')} />

      <div className="mb-3">
        <FieldLabel>{t('workflow:field.label')}</FieldLabel>
        <input className={inputCls} value={cfg.label ?? node.data?.label ?? ''}
          onChange={(e) => patch({ label: e.target.value })} placeholder={t('property:tl.labelPlaceholder')} />
      </div>

      <EndpointBox title="Source" ep={source} info={srcInfo} which="source" />
      <EndpointBox title="Target" ep={target} info={tgtInfo} which="target" />

      {/* 점검 — 실행 전에 논리적으로 어긋난 곳 */}
      <Section
        title={<>{t('property:check.title')} {errors > 0 && <span className="ml-1 text-red-600">{t('property:check.errors', { n: errors })}</span>}{warns > 0 && <span className="ml-1 text-amber-600">{t('property:check.warns', { n: warns })}</span>}</>}
        right={hasColumnChange ? (
          <button type="button" className={smallBtn} onClick={() => patch({ mappingSyncedAt: new Date().toISOString() })}
            title={t('property:check.ackTitle')}>{t('property:check.ack')}</button>
        ) : undefined}
        className={errors ? 'border-red-200' : warns ? 'border-amber-200' : ''}
      >
        {issues.filter((i) => i.level !== 'info').length === 0 ? (
          <div className="text-xs text-green-700">{t('property:check.ok')}</div>
        ) : null}
        <ul className="space-y-1">
          {issues.map((it, i) => (
            <li key={i} className={`text-[0.6875rem] leading-4 flex gap-1 ${it.level === 'error' ? 'text-red-700' : it.level === 'warn' ? 'text-amber-800' : 'text-gray-500'}`}>
              <span className="shrink-0">{it.level === 'error' ? '✕' : it.level === 'warn' ? '!' : 'ⓘ'}</span>
              <span>{it.message}</span>
            </li>
          ))}
        </ul>
      </Section>

      {source?.kind === 'db' && (
        <Section title={t('property:extract.title')}>
          <div className="flex items-center gap-4 text-xs text-gray-700 mb-1.5">
            <label className="flex items-center gap-1 cursor-pointer"><input type="radio" name={`em-${node.id}`} checked={cfg.extractMode !== 'QUERY'} onChange={() => patch({ extractMode: 'COLUMNS' })} />{t('property:extract.allColumns')}</label>
            <label className="flex items-center gap-1 cursor-pointer"><input type="radio" name={`em-${node.id}`} checked={cfg.extractMode === 'QUERY'} onChange={chooseQueryMode} />{t('property:extract.query')}</label>
          </div>
          {cfg.extractMode === 'QUERY' && (
            <>
              <pre onClick={() => setSqlOpen(true)} title={t('property:extract.clickToEdit')}
                className="w-full max-h-28 overflow-auto border border-gray-300 rounded px-2 py-1 text-[0.6875rem] font-mono bg-gray-50 text-gray-800 whitespace-pre-wrap cursor-text">
                {(cfg.query || '').trim() || t('property:extract.empty')}
              </pre>
              <div className="flex items-center gap-2 mt-1">
                <button type="button" className={primaryBtn} onClick={() => setSqlOpen(true)}>{t('property:extract.editSql')}</button>
                <button type="button" className={smallBtn} onClick={() => patch({ query: draftSelect(source, srcInfo.columns) })}
                  title={t('property:extract.redraftTitle')}>{t('property:extract.redraft')}</button>
              </div>
              <div className="mt-1 text-[0.6875rem] text-gray-500">{t('property:extract.runsOnSource')}</div>
            </>
          )}
        </Section>
      )}

      <Section title={t('property:mapping.title')}>
        <div className="flex items-center gap-4 text-xs text-gray-700 mb-1.5">
          <label className="flex items-center gap-1 cursor-pointer"><input type="radio" name={`tm-${node.id}`} checked={transformMode === 'MOVE'} onChange={() => patch({ transformMode: 'MOVE' })} />{t('property:mapping.move')}</label>
          <label className="flex items-center gap-1 cursor-pointer"><input type="radio" name={`tm-${node.id}`} checked={transformMode === 'MAPPING'} onChange={() => patch({ transformMode: 'MAPPING' })} />{t('property:mapping.mapping')}</label>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" className={primaryBtn} disabled={transformMode !== 'MAPPING'} onClick={openManual}>{t('property:mapping.define')}</button>
          <button type="button" className={smallBtn} disabled={transformMode !== 'MAPPING' || !projectId} onClick={() => setPickerOpen(true)}
            title={t('mapping:node.fromSpecTitle')}>{t('mapping:node.fromSpec')}</button>
          <span className="text-xs text-gray-500">{transformMode === 'MAPPING' ? (mapping.length ? t('property:mapping.defined', { n: mapping.length }) : t('property:mapping.none')) : t('property:mapping.moveHint')}</span>
        </div>
        {transformMode === 'MAPPING' && specRef && (
          <div className="mt-1.5 text-[0.6875rem] leading-4">
            <span className="text-gray-700">{t('mapping:node.linked', { id: specRef.mappingId, v: specRef.version })}</span>
            {specState?.missing && <div className="text-red-600">{t('mapping:node.missing', { id: specRef.mappingId, v: specState.latest })}</div>}
            {specState && !specState.missing && specState.latest > specRef.version && specState.changed > 0 && (
              <div className="flex items-center gap-1 text-amber-700">
                {t('mapping:node.newer', { v: specState.latest, n: specState.changed })}
                <button type="button" className={smallBtn} onClick={refreshFromSpec}>{t('mapping:node.refresh')}</button>
              </div>
            )}
          </div>
        )}
        {transformMode === 'MAPPING' && mapping.length > 0 && (
          <ul className="mt-1.5 max-h-28 overflow-auto text-[0.6875rem] text-gray-600 font-mono leading-4">
            {mapping.map((m, i) => <li key={i} className="truncate">{m.target} ← {m.expr || m.source || t('property:mapping.emptySource')}</li>)}
          </ul>
        )}
      </Section>

      {/* 예외 흐름 — 이 처리가 실패했을 때 갈 길. 켜면 노드에 빨간 핸들이 하나 더 생긴다 (utils/errorFlow) */}
      <Section title={t('property:tl.errorFlowTitle')}>
        <label className="flex items-center gap-1.5 text-xs text-gray-700 cursor-pointer">
          <input type="checkbox" checked={!!cfg.errorFlow} onChange={(e) => patch({ errorFlow: e.target.checked })} />
          {t('property:tl.errorFlowUse')}
        </label>
        <div className="mt-1 text-[0.6875rem] text-gray-500 leading-relaxed">{t('property:tl.errorFlowHint')}</div>
        {errorTargets.length > 0 && (
          <div className="mt-1 text-[0.6875rem] text-gray-600">
            {t('property:tl.errorFlowTargets', { names: errorTargets.join(', ') })}
          </div>
        )}
      </Section>

      {target && target.kind !== 'inner' && (
        <Section title={t('property:load.title')}>
          <div className="flex items-center gap-4 text-xs text-gray-700 mb-1.5 flex-wrap">
            <label className="flex items-center gap-1 cursor-pointer"><input type="radio" name={`md-${node.id}`} checked={mode === 'WRITE'} onChange={() => patch({ mode: 'WRITE' })} />{target.kind === 'db' ? t('property:load.writeDb') : t('property:load.writeFile')}</label>
            <label className="flex items-center gap-1 cursor-pointer"><input type="radio" name={`md-${node.id}`} checked={mode === 'APPEND'} onChange={() => patch({ mode: 'APPEND' })} />{t('property:load.append')}</label>
            {target.kind === 'db' && <label className="flex items-center gap-1 cursor-pointer"><input type="radio" name={`md-${node.id}`} checked={mode === 'UPSERT'} onChange={() => patch({ mode: 'UPSERT' })} />{t('property:load.upsert')}</label>}
          </div>
          {target.kind === 'db' && mode === 'UPSERT' && (
            <div className="mb-1.5">
              <div className="text-[0.6875rem] text-gray-500 mb-0.5">{t('property:load.keyHint')}</div>
              <div className="flex flex-wrap gap-1 mb-1">
                {(cfg.keyColumns || []).map((k: string, i: number) => <span key={k} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-gray-100 text-xs font-mono text-gray-700">{k}<button className="text-gray-400 hover:text-red-500" onClick={() => patch({ keyColumns: cfg.keyColumns.filter((_: string, j: number) => j !== i) })}>✕</button></span>)}
              </div>
              <select className="border border-gray-300 rounded px-1.5 py-0.5 text-xs bg-white" value="" onChange={(e) => { if (e.target.value) patch({ keyColumns: [...(cfg.keyColumns || []), e.target.value] }); }}>
                <option value="">{t('property:load.addKey')}</option>
                {tgtInfo.columns.map((c) => c.name).filter((n) => !(cfg.keyColumns || []).includes(n)).map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
          )}
          {target.kind === 'db' && (
            <label className={`flex items-center gap-1.5 text-xs ${bulk.ok ? 'text-gray-700 cursor-pointer' : 'text-gray-400'}`} title={bulk.label}>
              <input type="checkbox" checked={!!cfg.useBulkLoader} disabled={!bulk.ok} onChange={(e) => patch({ useBulkLoader: e.target.checked })} />
              <span className="truncate">{bulk.label}</span>
            </label>
          )}
          {target.kind === 'db' && directOk && mode !== 'UPSERT' && !cfg.useBulkLoader && (
            <label className="flex items-start gap-1.5 text-xs text-gray-700 cursor-pointer mt-1" title={t('property:load.directPathTitle')}>
              <input type="checkbox" className="mt-0.5" checked={!!cfg.directPath} onChange={(e) => patch({ directPath: e.target.checked })} />
              <span>{t('property:load.directPath')}<span className="block text-[0.6875rem] text-gray-500">{t('property:load.directPathHint')}</span></span>
            </label>
          )}
        </Section>
      )}

      {/* 분할처리 — 기본은 접어 둔다(자주 쓰지 않고 칸이 길다). 고른 방식대로 세부 흐름을 만들고 읽기 전용 그룹으로 묶는다 */}
      <Collapsible title={t('property:split.title')} summary={splitModes.find((m) => m.mode === splitSpec.mode)?.label}>
        <div className="text-[0.6875rem] text-gray-500 mb-1.5">
          {t('property:split.intro')}
        </div>
        <div className="text-[0.6875rem] text-gray-500 mb-1.5">
          {t('property:split.current')} <b>{t(KIND_KEY[splitCtx.srcKind])}</b> → <b>{t(KIND_KEY[splitCtx.tgtKind])}</b>
          {splitCtx.dbRelation !== 'n/a' && REL_KEY[splitCtx.dbRelation] && <> · {t(REL_KEY[splitCtx.dbRelation])}</>}
        </div>

        <div className="space-y-1">
          {splitModes.map((m) => (
            <label key={m.mode} className={`flex items-start gap-1.5 text-xs ${m.blocked ? 'text-gray-400' : 'text-gray-700 cursor-pointer'}`} title={m.blocked || m.desc}>
              <input type="radio" name={`split-${node.id}`} className="mt-0.5" disabled={!!m.blocked}
                checked={splitSpec.mode === m.mode} onChange={() => setSplitSpec({ ...splitSpec, mode: m.mode })} />
              <span className="min-w-0">
                <span className="font-medium">{m.label}</span>
                <span className="block text-[0.6875rem] text-gray-500">{m.blocked || m.desc}</span>
              </span>
            </label>
          ))}
        </div>

        {splitSpec.mode !== 'none' && (
          <div className="mt-2 flex flex-wrap items-end gap-2">
            {splitNeeds.includes('count') && (
              <label className="flex flex-col gap-0.5 text-[0.6875rem] text-gray-600">{t('property:split.count')}
                <input type="number" min={2} max={32} className="border border-gray-300 rounded px-1.5 py-0.5 text-xs w-16"
                  value={splitSpec.count} onChange={(e) => setSplitSpec({ ...splitSpec, count: Number(e.target.value) })} />
              </label>
            )}
            {splitNeeds.includes('keyColumn') && (
              <label className="flex flex-col gap-0.5 text-[0.6875rem] text-gray-600">{t('property:split.keyColumn')}
                <select className="border border-gray-300 rounded px-1.5 py-0.5 text-xs bg-white"
                  value={splitSpec.keyColumn} onChange={(e) => setSplitSpec({ ...splitSpec, keyColumn: e.target.value })}>
                  <option value="">{t('property:split.choose')}</option>
                  {srcInfo.columns.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
                </select>
              </label>
            )}
            {splitNeeds.includes('dbLinkName') && (
              <label className="flex flex-col gap-0.5 text-[0.6875rem] text-gray-600">{t('property:split.dbLink')}
                <input className="border border-gray-300 rounded px-1.5 py-0.5 text-xs w-28"
                  value={splitSpec.dbLinkName || ''} onChange={(e) => setSplitSpec({ ...splitSpec, dbLinkName: e.target.value })} placeholder="TGT_LINK" />
              </label>
            )}
            {splitNeeds.includes('tempPrefix') && (
              <label className="flex flex-col gap-0.5 text-[0.6875rem] text-gray-600">{t('property:split.tempPrefix')}
                <input className="border border-gray-300 rounded px-1.5 py-0.5 text-xs w-24"
                  value={splitSpec.tempPrefix || ''} onChange={(e) => setSplitSpec({ ...splitSpec, tempPrefix: e.target.value })} placeholder="of_tmp" />
              </label>
            )}
            {splitNeeds.includes('stagingPath') && (
              <label className="flex flex-col gap-0.5 text-[0.6875rem] text-gray-600">{t('property:split.stagingPath')}
                <input className="border border-gray-300 rounded px-1.5 py-0.5 text-xs w-52"
                  value={splitSpec.stagingPath || ''} onChange={(e) => setSplitSpec({ ...splitSpec, stagingPath: e.target.value })} placeholder="out/staging_target.csv" />
              </label>
            )}
            <button type="button" className={primaryBtn} disabled={!!splitBlockedReason}
              title={splitBlockedReason || t('property:split.applyTitle')} onClick={applySplit}>
              {t('property:split.apply')}
            </button>
          </div>
        )}
        {splitSpec.mode !== 'none' && splitBlockedReason && (
          <div className="mt-1 text-[0.6875rem] text-amber-700">{splitBlockedReason}</div>
        )}
      </Collapsible>

      {detail && (
        <DataDetailModal
          title={t(detail === 'source' ? 'property:detail.source' : 'property:detail.target', { kind: (detail === 'source' ? source : target)?.kindLabel || '' })}
          subtitle={(detail === 'source' ? source : target)?.name}
          columns={(detail === 'source' ? srcInfo : tgtInfo).columns}
          loading={(detail === 'source' ? srcInfo : tgtInfo).loading}
          error={(detail === 'source' ? srcInfo : tgtInfo).error}
          preview={(() => { const ep = detail === 'source' ? source : target; if (!ep || ep.kind === 'inner') return null; const c = ep.node.data?.config || {};
            return ep.kind === 'db' ? { kind: 'db' as const, connection: c.connection, schema: c.schema, table: c.table } : { kind: 'csv' as const, filepath: c.filepath, delimiter: c.delimiter || ',', hasHeader: c.hasHeader !== false, encoding: c.encoding || 'UTF-8' }; })()}
          onClose={() => setDetail(null)}
        />
      )}
      {mappingOpen && (
        <TransformMappingModal
          sourceRows={sourceRows}
          sourceColumns={srcInfo.columns}
          targetColumns={target && target.kind !== 'inner' ? tgtInfo.columns : []}
          initial={mapping}
          onSave={(m) => { patch({ mapping: m, transformMode: 'MAPPING', mappingSyncedAt: new Date().toISOString(), mappingSpec: undefined }); setMappingOpen(false); }}
          onClose={() => setMappingOpen(false)}
        />
      )}
      {pickerOpen && projectId && (
        <MappingSpecPicker projectId={specRef?.projectId || projectId} current={specRef?.mappingId}
          target={target?.kind === 'db' ? { schema: target.node.data?.config?.schema, table: target.node.data?.config?.table } : undefined}
          onPick={applySpec} onClose={() => setPickerOpen(false)} />
      )}
      {sqlOpen && source?.kind === 'db' && (
        <SqlEditorModal
          title={t('property:extract.sqlModalTitle')}
          value={cfg.query || draftSelect(source, srcInfo.columns)}
          connection={source.node.data?.config?.connection}
          onSave={(sql) => { patch({ query: sql, extractMode: 'QUERY' }); setSqlOpen(false); }}
          onClose={() => setSqlOpen(false)}
        />
      )}
    </div>
  );
};

export default TransformLoadPanel;
